// Manual questionnaire entry: the nurse-facing rendering of the same v2
// template the AI call runs. Full-page outline + focused-section layout
// modeled on the One Medical Passport flow VIP's team already knows —
// section outline with live status on the left, one section at a time on the
// right. Conditional follow-ups come from the shared interview runtime; saves
// go through the binding table into the patient record (which feeds scoring
// and the chart pack automatically). Used for refusers, hang-ups, and
// unreachable patients (spec [D2]).
//
// All colors come from the active workforce theme — no hardcoded palette
// values (packages/design-tokens/README.md).

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";

import type { designTokens } from "@amplecare/design-tokens";

import {
  evaluateQuestionnaireV1,
  parseAnswerNormalizationV1,
  type ClinicianWorkbenchActionInput,
  type ClinicianWorkbenchOutboundManualAnswerV1,
  type ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1,
  type ClinicianWorkbenchPatientRecordV1,
  type ClinicianWorkbenchQuestionnaireFieldBinding,
  type TenantAdminOutboundQuestionnaireQuestionV1,
} from "@amplecare/contracts";

import type { AppShellTheme } from "../shell-core.js";
import { LineIcon } from "../line-icons.js";
import {
  filterQuestionnaireSelectOptions,
  hydrateQuestionnaireSelectValues,
  parseIsoDateText,
  type QuestionnaireSelectOption,
  type QuestionnaireSelectValue,
} from "../shell-field-primitives.js";
import {
  workforceConfigurationDialog,
  workforceOperationalLayout,
  workforceOperationalTypography,
  workforceResponsiveBreakpoints,
} from "../workforce-design-system.js";
import {
  answerReviewSignal,
  areQuestionnaireSectionAnswerSnapshotsEqual,
  canonicalizeQuestionnaireUiAnswers,
  encodeQuestionnaireAnswerSnapshot,
  type QuestionnaireSaveIssue,
  buildQuestionnaireDirtySectionKeys,
  buildOutboundPrefillAnswers,
  buildRecordQuestionnaireAnswers,
  buildQuestionSectionIndex,
  computeQuestionnaireSectionStats,
  countSavedCompleteSections,
  countUnsavedSections,
  questionnaireOptionsAreUnconfirmed,
  resolveManualCompletionState,
  resolveQuestionnairePromptText,
  resolveQuestionnaireReviewPromptText,
  shouldBlockUnresolvedQuestionnairePrompt,
  type OutboundPrefillResult,
} from "./questionnaire-entry-core.js";
import { isClinicianLiveCallSessionStatus } from "./presentation.js";
import {
  buildMonthGrid,
  formatMonthTitleText,
} from "./list-calendar.js";
import {
  resolveHorizontalTabNavigationIndex,
  resolveRovingFocusIndex,
} from "./keyboard-navigation.js";
import { computeAgeYears } from "./record/scoring.js";
import { validateRecordValue } from "./record/validation.js";
import { getPatientRecordValueByPath } from "./record/record-core.js";
import {
  buildQuestionnaireRecordUpsert,
  filterQuestionnaireProjectionClearKeys,
} from "./record/questionnaire-upsert.js";

type QuestionnaireTabKeyDownEvent = {
  currentTarget?: unknown;
  key?: string;
  nativeEvent?: { key?: string };
  preventDefault?: () => void;
};

function handleQuestionnaireTabKeyDown(
  event: QuestionnaireTabKeyDownEvent,
  currentIndex: number,
  sectionKeys: readonly string[],
  onSelect: (sectionKey: string) => void,
): void {
  const nextIndex = resolveHorizontalTabNavigationIndex(
    event.key ?? event.nativeEvent?.key ?? "",
    currentIndex,
    sectionKeys.length,
  );
  const nextSectionKey =
    nextIndex == null ? undefined : sectionKeys[nextIndex];

  if (nextIndex == null || nextSectionKey == null) {
    return;
  }

  event.preventDefault?.();
  onSelect(nextSectionKey);

  const currentTarget = event.currentTarget as HTMLElement | null | undefined;
  const tabList = currentTarget?.closest?.('[role="tablist"]');

  setTimeout(() => {
    tabList
      ?.querySelectorAll<HTMLElement>('[role="tab"]')
      .item(nextIndex)
      ?.focus();
  }, 0);
}

export interface ClinicianQuestionnaireEntrySurfaceProps {
  visible: boolean;
  patientId: string;
  patientDisplayName: string;
  patientIdentityText?: string | null;
  materialChangeToken: string;
  record: ClinicianWorkbenchPatientRecordV1;
  /** Tenant-served template questions (backend questionnaire definition) —
   *  the surface renders whatever template the tenant has configured. */
  questions: TenantAdminOutboundQuestionnaireQuestionV1[];
  /** The template version's chart bindings (answers → record fields). */
  chartBindings: ClinicianWorkbenchQuestionnaireFieldBinding[];
  isSubmitting: boolean;
  theme: AppShellTheme;
  layout: (typeof designTokens)["semanticLayout"];
  onSubmitWorkbenchAction: (
    action: ClinicianWorkbenchActionInput,
    options?: { onSuccess?: () => void; onError?: (message: string) => void },
  ) => void;
  onDirtyStateChange: (dirty: boolean) => void;
  onClose: () => void;
  /** Active (unfinished) AI-call session for this patient, when one exists —
   *  enables phone-answer prefill and the manual-completion flow. */
  outboundSession?: {
    targetId: string;
    sessionId: string;
    sessionStatus: string;
    sessionRevision: string | null;
    answeredQuestionCount: number;
    totalQuestionCount: number;
  } | null;
  /** The active session's per-question answer previews (prefill source). */
  outboundAnswerPreview?: ClinicianWorkbenchOutboundQuestionnaireAnswerPreviewV1[];
  /** Mirrors clinician answers into the outbound session / completes it. */
  onSubmitOutboundManualCommand?: (
    command: {
      action: "record-manual-answers" | "complete-session-manually";
      targetId: string;
      sessionId: string;
      manualAnswers: ClinicianWorkbenchOutboundManualAnswerV1[];
      clearedQuestionKeys: string[];
      expectedSessionRevision: string;
    },
    options?: {
      onError?: (message: string) => void;
      onSuccess?: (
        summaryText: string,
        sessionRevision: string | null,
      ) => void;
    },
  ) => void;
  isOutboundCommandSubmitting?: boolean;
}

type Answers = Record<string, unknown>;
type SectionState = "complete" | "in-progress" | "attention" | "untouched";

interface SectionView {
  key: string;
  label: string;
  questions: TenantAdminOutboundQuestionnaireQuestionV1[];
}

export function ClinicianQuestionnaireEntrySurface({
  visible,
  patientId,
  patientDisplayName,
  patientIdentityText,
  materialChangeToken,
  record,
  questions,
  chartBindings,
  isSubmitting,
  theme,
  layout,
  onSubmitWorkbenchAction,
  onDirtyStateChange,
  onClose,
  outboundSession,
  outboundAnswerPreview,
  onSubmitOutboundManualCommand,
  isOutboundCommandSubmitting,
}: ClinicianQuestionnaireEntrySurfaceProps): React.JSX.Element {
  const { width: viewportWidth } = useWindowDimensions();
  const isCompactSurface =
    viewportWidth < workforceResponsiveBreakpoints.compact;
  const isMediumSurface =
    viewportWidth < workforceResponsiveBreakpoints.wide;
  const controlHeight = isMediumSurface
    ? workforceOperationalLayout.mobileControlHeight
    : workforceConfigurationDialog.controlHeight;
  const formBusy =
    isSubmitting || isOutboundCommandSubmitting === true;
  const contentPadding = isCompactSurface
    ? workforceConfigurationDialog.paddingCompact
    : workforceConfigurationDialog.paddingWide;
  const expandedRailWidth = isMediumSurface ? 216 : 240;

  // Phone-call prefill (GP workflow): an interrupted AI call's answers seed
  // the form so the clinician continues instead of starting over. Computed
  // when the surface OPENS (the outbound data loads asynchronously after the
  // profile mounts, and the session can change while the modal sits hidden),
  // and it only fills gaps — clinician-entered values always win.
  const [prefill, setPrefill] = useState<OutboundPrefillResult>({
    provenance: {},
    uiAnswers: {},
  });
  const [answers, setAnswers] = useState<Answers>({});
  const [committedAnswers, setCommittedAnswers] = useState<Answers>({});
  const answersRef = useRef<Answers>(answers);
  answersRef.current = answers;

  useEffect(() => {
    if (!visible) {
      return;
    }

    const next =
      (outboundAnswerPreview?.length ?? 0) > 0
        ? buildOutboundPrefillAnswers({
            questions,
            previews: outboundAnswerPreview ?? [],
          })
        : { provenance: {}, uiAnswers: {} };
    // Answers from a finished call live on the record, not on an open session.
    // Seed from the record first so the form opens populated, then let the
    // live-session previews win where both have a value.
    const recordAnswers = buildRecordQuestionnaireAnswers({
      questions,
      bindings: chartBindings,
      getFieldValue: (fieldPath) =>
        getPatientRecordValueByPath(record, fieldPath),
    });

    next.uiAnswers = { ...recordAnswers, ...next.uiAnswers } as typeof next.uiAnswers;

    setPrefill(next);
    setAnswers((current) => {
      const merged = { ...next.uiAnswers, ...current };
      answersRef.current = merged;
      return merged;
    });
    const canonicalPrefill = canonicalizeQuestionnaireUiAnswers({
      answers: next.uiAnswers,
      questions,
    });
    setCommittedAnswers((current) => ({ ...canonicalPrefill, ...current }));
  }, [visible, outboundSession, outboundAnswerPreview, questions, chartBindings, record]);
  const [savedSections, setSavedSections] = useState<Set<string>>(new Set());
  const [committedSections, setCommittedSections] = useState<Set<string>>(
    new Set(),
  );
  const [transientDirtyQuestionKeys, setTransientDirtyQuestionKeys] = useState<
    Set<string>
  >(
    new Set(),
  );
  const [sectionIssues, setSectionIssues] = useState<
    Record<string, QuestionnaireSaveIssue[]>
  >({});
  const [activeSectionKey, setActiveSectionKey] = useState<string | null>(null);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [completeArmed, setCompleteArmed] = useState(false);
  const [completionNotice, setCompletionNotice] = useState<string | null>(null);
  const [saveErrorText, setSaveErrorText] = useState<
    string | null
  >(null);
  const outboundSessionRevisionRef = useRef(
    outboundSession?.sessionRevision ?? null,
  );
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [formRevision, setFormRevision] = useState(0);
  const closeConfirmationPromptRef = useRef<View | null>(null);
  const closeConfirmationStayButtonRef = useRef<View | null>(null);

  useEffect(() => {
    if (!showCloseConfirmation) {
      return;
    }

    const focusTimeout = setTimeout(() => {
      const stayButton =
        closeConfirmationStayButtonRef.current as unknown as {
          focus?: () => void;
        } | null;

      stayButton?.focus?.();
    }, 0);

    if (typeof document === "undefined") {
      return () => clearTimeout(focusTimeout);
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowCloseConfirmation(false);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const promptNode =
        closeConfirmationPromptRef.current as unknown as HTMLElement | null;
      const focusableElements = promptNode
        ? Array.from(
            promptNode.querySelectorAll<HTMLElement>(
              '[role="button"]:not([aria-disabled="true"]), button:not([disabled]), [tabindex="0"]',
            ),
          )
        : [];

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      clearTimeout(focusTimeout);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [showCloseConfirmation]);

  useEffect(() => {
    if (isCompactSurface) {
      setRailCollapsed(false);
    }
  }, [isCompactSurface]);

  useEffect(() => {
    if (!visible) {
      setShowCloseConfirmation(false);
    }
  }, [visible]);

  const context = useMemo(
    () => ({
      ageYears: computeAgeYears(
        record.patient.dateOfBirth,
        record.case.dateOfService,
      ),
      sexAtBirth: record.patient.sex,
    }),
    [record.patient.dateOfBirth, record.case.dateOfService, record.patient.sex],
  );
  const capturedOutboundQuestionKeys = useMemo(() => {
    if (outboundSession == null) {
      return new Set<string>();
    }

    return new Set(
      Object.keys(
        buildOutboundPrefillAnswers({
          questions,
          previews: outboundAnswerPreview ?? [],
        }).provenance,
      ),
    );
  }, [outboundSession, outboundAnswerPreview, questions]);
  const unavailableQuestionKeys = useMemo(() => {
    return new Set(
      questions
        .filter(
          (question) =>
            shouldBlockUnresolvedQuestionnairePrompt({
              hasCapturedOutboundAnswer: capturedOutboundQuestionKeys.has(
                question.questionKey,
              ),
              promptText: question.promptText ?? question.questionKey,
              record,
            }),
        )
        .map((question) => question.questionKey),
    );
  }, [capturedOutboundQuestionKeys, questions, record]);

  // Evaluate against canonical values, not UI wrapper objects — string/oneOf
  // gate conditions must see the option value the template declares.
  const canonicalAnswers = useMemo(() => {
    return canonicalizeQuestionnaireUiAnswers({
      answers,
      questions,
      unavailableQuestionKeys,
    });
  }, [questions, answers, unavailableQuestionKeys]);

  // Canonical snapshot of the prefilled values — the per-question "edited"
  // marker compares against this original.
  const prefillCanonicalValues = useMemo<Answers>(() => {
    return canonicalizeQuestionnaireUiAnswers({
      answers: prefill.uiAnswers,
      questions,
    });
  }, [questions, prefill]);

  const evaluation = useMemo(
    () => evaluateQuestionnaireV1(questions, canonicalAnswers, context),
    [questions, canonicalAnswers, context],
  );
  const visibleSet = useMemo(
    () => new Set(evaluation.visibleQuestionKeys),
    [evaluation.visibleQuestionKeys],
  );

  const sections = useMemo<SectionView[]>(() => {
    const bySection = new Map<string, SectionView>();
    for (const question of questions) {
      const entry = bySection.get(question.sectionKey) ?? {
        key: question.sectionKey,
        label: question.sectionLabel ?? question.sectionKey,
        questions: [] as TenantAdminOutboundQuestionnaireQuestionV1[],
      };
      entry.questions.push(question);
      bySection.set(question.sectionKey, entry);
    }
    return [...bySection.values()];
  }, [questions]);

  const questionSectionIndex = useMemo(
    () => buildQuestionSectionIndex(sections),
    [sections],
  );
  const effectiveAnswers = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(canonicalAnswers).filter(([questionKey]) =>
          visibleSet.has(questionKey),
        ),
      ),
    [canonicalAnswers, visibleSet],
  );
  const dirtySectionKeys = useMemo(
    () =>
      buildQuestionnaireDirtySectionKeys({
        committedAnswers,
        effectiveAnswers,
        questionSectionIndex,
        transientDirtyQuestionKeys,
        visibleQuestionKeys: visibleSet,
      }),
    [
      committedAnswers,
      effectiveAnswers,
      questionSectionIndex,
      transientDirtyQuestionKeys,
      visibleSet,
    ],
  );
  const displayedSavedSections = useMemo(() => {
    const next = new Set(savedSections);

    dirtySectionKeys.forEach((sectionKey) => next.delete(sectionKey));

    return next;
  }, [dirtySectionKeys, savedSections]);
  const sectionStats = useMemo(
    () =>
      computeQuestionnaireSectionStats({
        sections,
        visibleKeys: visibleSet,
        answers: canonicalAnswers,
        savedSections: displayedSavedSections,
        sectionIssues,
      }),
    [
      sections,
      visibleSet,
      canonicalAnswers,
      displayedSavedSections,
      sectionIssues,
    ],
  );

  const firstIncompleteKey =
    sections.find((section) => sectionStats.get(section.key)?.state !== "complete")?.key ??
    sections[0]?.key ??
    null;
  const activeKey = activeSectionKey ?? firstIncompleteKey;
  const activeSection = sections.find((section) => section.key === activeKey) ?? sections[0];
  // Only persisted work counts in the headline number.
  const completeCount = countSavedCompleteSections(sectionStats);
  const dirtyCount = dirtySectionKeys.size;
  const unsavedSectionCount = countUnsavedSections(sectionStats);
  const validationIssueCount = Object.values(sectionIssues).reduce(
    (total, entries) => total + entries.length,
    0,
  );
  const activeSectionIssues = activeSection
    ? (sectionIssues[activeSection.key] ?? [])
    : [];
  const activeSectionIssueByQuestionKey = new Map<
    string,
    QuestionnaireSaveIssue
  >();
  for (const issue of activeSectionIssues) {
    if (!activeSectionIssueByQuestionKey.has(issue.questionKey)) {
      activeSectionIssueByQuestionKey.set(issue.questionKey, issue);
    }
  }
  const requiredMissingCount = evaluation.requiredMissingKeys.length;
  const {
    canComplete: canCompleteManually,
    blockText: manualCompletionBlockText,
  } = resolveManualCompletionState({
    isLiveCallSession: isClinicianLiveCallSessionStatus(
      outboundSession?.sessionStatus,
    ),
    dirtyCount,
    unsavedSectionCount,
    validationIssueCount,
    requiredMissingCount,
    isOutboundCommandSubmitting,
  });

  useEffect(() => {
    onDirtyStateChange(dirtyCount > 0);
  }, [dirtyCount, onDirtyStateChange]);

  useEffect(
    () => () => {
      onDirtyStateChange(false);
    },
    [onDirtyStateChange],
  );

  const setQuestionDraftDirty = (
    questionKey: string,
    dirty: boolean,
  ): void => {
    if (formBusy) {
      return;
    }

    setTransientDirtyQuestionKeys((current) => {
      const next = new Set(current);

      if (dirty) {
        next.add(questionKey);
      } else {
        next.delete(questionKey);
      }

      return next;
    });
  };

  const setAnswer = (questionKey: string, value: unknown) => {
    if (formBusy) {
      return;
    }

    // Pin the section the nurse is working in: without this, completing its
    // last question flips the derived "first incomplete" section and the view
    // jumps away before they can review or save.
    setActiveSectionKey((current) => current ?? activeKey);
    const nextValue =
      value === "" || value === undefined || value === null
        ? undefined
        : value;
    const previousValue = answersRef.current[questionKey];
    const answerChanged =
      !Object.is(previousValue, nextValue) &&
      JSON.stringify(previousValue) !== JSON.stringify(nextValue);

    setTransientDirtyQuestionKeys((current) => {
      if (!current.has(questionKey)) {
        return current;
      }

      const next = new Set(current);
      next.delete(questionKey);
      return next;
    });

    if (!answerChanged) {
      return;
    }

    const nextAnswers = { ...answersRef.current };
    // An empty text field is not an answer — storing "" would turn the section
    // green while the upsert builder writes nothing.
    if (nextValue === undefined) {
      delete nextAnswers[questionKey];
    } else {
      nextAnswers[questionKey] = nextValue;
    }
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    const sectionKey = questionSectionIndex.get(questionKey);
    if (sectionKey) {
      setSectionIssues((current) => {
        if (current[sectionKey] == null) {
          return current;
        }

        const next = { ...current };
        delete next[sectionKey];
        return next;
      });
    }
  };

  const commitSectionState = (
    section: SectionView,
    sectionAnswers: Answers,
  ): void => {
    setCommittedAnswers((current) => {
      const next = { ...current };
      for (const question of section.questions) {
        delete next[question.questionKey];
        if (sectionAnswers[question.questionKey] !== undefined) {
          next[question.questionKey] = sectionAnswers[question.questionKey];
        }
      }
      return next;
    });
    setTransientDirtyQuestionKeys((current) => {
      const next = new Set(current);

      section.questions.forEach((question) =>
        next.delete(question.questionKey),
      );

      return next;
    });
    setCommittedSections((current) => new Set(current).add(section.key));
  };

  const requestClose = (): void => {
    if (dirtySectionKeys.size > 0) {
      setShowCloseConfirmation(true);
      return;
    }
    onClose();
  };

  const discardAndClose = (): void => {
    answersRef.current = committedAnswers;
    setAnswers(committedAnswers);
    setSavedSections(new Set(committedSections));
    setTransientDirtyQuestionKeys(new Set());
    setCompleteArmed(false);
    setShowCloseConfirmation(false);
    setFormRevision((current) => current + 1);
    onClose();
  };

  const saveSection = (section: SectionView, advance: boolean) => {
    if (completionNotice != null || formBusy) {
      return;
    }

    const sectionAnswers: Answers = {};
    for (const question of section.questions) {
      // Orphan rule: only currently-visible answers are written.
      if (!visibleSet.has(question.questionKey)) continue;
      const value = canonicalAnswers[question.questionKey];
      if (value !== undefined) sectionAnswers[question.questionKey] = value;
    }
    const questionKeys = section.questions.map(
      (question) => question.questionKey,
    );
    const submittedAnswerSnapshot = Object.fromEntries(
      questionKeys.map((questionKey) => [
        questionKey,
        answersRef.current[questionKey],
      ]),
    );
    // A question the nurse blanked. A question that a gate has HIDDEN is not
    // one of these: the orphan rule above already drops invisible answers from
    // sectionAnswers, so without the visibleSet test every gate flip looked
    // like a deliberate clear — and if the record field came from anywhere but
    // the questionnaire, the refusal below counted it as an issue no nurse
    // action could resolve.
    const clearQuestionKeys = questionKeys.filter(
      (questionKey) =>
        visibleSet.has(questionKey) &&
        committedAnswers[questionKey] !== undefined &&
        sectionAnswers[questionKey] === undefined,
    );
    const protectedRecordClearQuestionKeys =
      filterQuestionnaireProjectionClearKeys({
        bindings: chartBindings,
        questionKeys: clearQuestionKeys,
        record,
      });
    const recordClearQuestionKeys =
      outboundSession != null
        ? clearQuestionKeys
        : protectedRecordClearQuestionKeys;
    const upsert = buildQuestionnaireRecordUpsert(
      sectionAnswers,
      chartBindings,
      { clearQuestionKeys: recordClearQuestionKeys },
    );
    // Every issue is recorded against the question the nurse typed into, not
    // just counted. The identities used to be computed here and dropped on the
    // same line, which is why the surface could say "2 answers need attention"
    // and had nothing to point at.
    const issues: QuestionnaireSaveIssue[] = [];
    const snapshotOf = (questionKey: string): string =>
      encodeQuestionnaireAnswerSnapshot(canonicalAnswers[questionKey]);
    const validPaths = upsert.touchedFieldPaths.filter((path) => {
      const okay =
        validateRecordValue(path, getPatientRecordValueByPath(upsert.record, path)) == null;
      if (!okay) {
        // A field path names a record slot, not a question, and several
        // questions can share one (three feed basicHistory.smoking.detail).
        // Flag all of its owners rather than guessing which half failed —
        // naming the wrong question is worse than admitting the ambiguity.
        const owners = upsert.fieldPathOwners[path] ?? [];
        for (const questionKey of owners) {
          issues.push({
            questionKey,
            reason: "invalid-value",
            sharedFieldPath: owners.length > 1,
            answerSnapshot: snapshotOf(questionKey),
          });
        }
      }
      return okay;
    });
    for (const questionKey of upsert.skippedAnswers) {
      issues.push({
        questionKey,
        reason: "no-binding",
        answerSnapshot: snapshotOf(questionKey),
      });
    }
    for (const entry of upsert.unparsedAnswers) {
      issues.push({
        questionKey: entry.questionKey,
        reason: "unreadable-answer",
        answerSnapshot: snapshotOf(entry.questionKey),
      });
    }
    if (outboundSession == null) {
      const allowed = new Set(protectedRecordClearQuestionKeys);
      for (const questionKey of clearQuestionKeys) {
        if (!allowed.has(questionKey)) {
          issues.push({
            questionKey,
            reason: "clear-refused",
            answerSnapshot: snapshotOf(questionKey),
          });
        }
      }
    }
    // A retry must not read as still-failing from the previous attempt.
    setSaveErrorText(null);
    setSectionIssues((current) => ({
      ...current,
      [section.key]: issues,
    }));

    if (issues.length > 0) {
      return;
    }

    const goNext = () => {
      if (!advance) return;
      const index = sections.findIndex((entry) => entry.key === section.key);
      const next = sections
        .slice(index + 1)
        .find((entry) => sectionStats.get(entry.key)?.state !== "complete");
      setActiveSectionKey((next ?? sections[index + 1] ?? section).key);
    };
    const finalizeSave = () => {
      // Commit unconditionally: the write already succeeded, so committedAnswers
      // must hold what was SENT. A question edited mid-flight is re-flagged by
      // the dirty computation and displayedSavedSections drops it from the saved
      // set — the state stays honest without a guard here. Gating this was the
      // old bug: it stranded persisted data behind a UI insisting otherwise.
      setSavedSections((current) => new Set(current).add(section.key));
      commitSectionState(section, sectionAnswers);
      // Navigation is the one thing a mid-flight edit should veto. The nurse's
      // most recent action (typing) outranks the earlier one (pressing next),
      // and losing your cursor mid-sentence in a clinical form is worse than
      // one un-advanced section, which the rail makes recoverable.
      if (
        areQuestionnaireSectionAnswerSnapshotsEqual(
          questionKeys,
          submittedAnswerSnapshot,
          answersRef.current,
        )
      ) {
        goNext();
      }
    };
    const mirrorAndFinalize = () => {
      setSaveErrorText(null);
      const started = mirrorAnswersToOutboundSession(
        sectionAnswers,
        false,
        clearQuestionKeys,
        {
          onError: setSaveErrorText,
          onSuccess: finalizeSave,
        },
      );

      if (!started) {
        finalizeSave();
      }
    };

    if (outboundSession != null) {
      mirrorAndFinalize();
      return;
    }

    if (validPaths.length === 0) {
      finalizeSave();
      return;
    }

    onSubmitWorkbenchAction(
      {
        kind: "upsert-patient-record",
        patientId,
        expectedMaterialChangeToken: materialChangeToken,
        record: upsert.record as never,
        touchedFieldPaths: validPaths,
        // Declare the writer so fieldMeta.source records that the questionnaire
        // wrote this. Without it these writes stamp "manual" — indistinguishable
        // from a profile edit — and the clear rule then refuses to let the form
        // clear its own saves.
        writerSource: "questionnaire",
      },
      {
        onError: setSaveErrorText,
        onSuccess: finalizeSave,
      },
    );
  };

  const toManualAnswers = (
    source: Answers,
  ): ClinicianWorkbenchOutboundManualAnswerV1[] =>
    Object.entries(source)
      .filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          (typeof value !== "string" || value.trim().length > 0),
      )
      .map(([questionKey, value]) => ({
        questionKey,
        value: value as ClinicianWorkbenchOutboundManualAnswerV1["value"],
      }));

  const mirrorAnswersToOutboundSession = (
    source: Answers,
    complete: boolean,
    clearedQuestionKeys: readonly string[] = [],
    options?: {
      onError?: (message: string) => void;
      onSuccess?: (
        summaryText: string,
        sessionRevision: string | null,
      ) => void;
    },
  ): boolean => {
    if (outboundSession == null || onSubmitOutboundManualCommand == null) {
      return false;
    }

    const expectedSessionRevision = outboundSessionRevisionRef.current;

    if (!expectedSessionRevision) {
      const errorText =
        "The active questionnaire needs to be refreshed before answers can be saved.";
      setSaveErrorText(errorText);
      options?.onError?.(errorText);
      return true;
    }

    const manualAnswers = toManualAnswers(source);
    const normalizedClearKeys = [
      ...new Set(
        clearedQuestionKeys.filter(
          (questionKey) =>
            questionKey.trim().length > 0 &&
            !manualAnswers.some(
              (answer) => answer.questionKey === questionKey,
            ),
        ),
      ),
    ];

    if (
      manualAnswers.length === 0 &&
      normalizedClearKeys.length === 0 &&
      !complete
    ) {
      return false;
    }

    onSubmitOutboundManualCommand(
      {
        action: complete ? "complete-session-manually" : "record-manual-answers",
        clearedQuestionKeys: normalizedClearKeys,
        expectedSessionRevision,
        manualAnswers,
        sessionId: outboundSession.sessionId,
        targetId: outboundSession.targetId,
      },
      complete
        ? {
            onSuccess: (summaryText, sessionRevision) => {
              outboundSessionRevisionRef.current = sessionRevision;
              setSaveErrorText(null);
              setCompleteArmed(false);
              setCompletionNotice(summaryText);
              setShowCloseConfirmation(false);
              options?.onSuccess?.(summaryText, sessionRevision);
              onClose();
            },
            onError: (message) => {
              setSaveErrorText(message);
              options?.onError?.(message);
            },
          }
        : {
            onError: (message) => {
              setSaveErrorText(message);
              options?.onError?.(message);
            },
            onSuccess: (summaryText, sessionRevision) => {
              outboundSessionRevisionRef.current = sessionRevision;
              setSaveErrorText(null);
              options?.onSuccess?.(summaryText, sessionRevision);
            },
          },
    );

    return true;
  };

  const completeManually = (): void => {
    if (!canCompleteManually) {
      return;
    }

    // Orphan rule as in saveSection: only currently-visible answers travel.
    const visibleAnswers: Answers = {};
    for (const [questionKey, value] of Object.entries(canonicalAnswers)) {
      if (visibleSet.has(questionKey)) visibleAnswers[questionKey] = value;
    }
    setSaveErrorText(null);
    mirrorAnswersToOutboundSession(visibleAnswers, true, [], {
      onError: setSaveErrorText,
    });
  };

  const stateColor = (state: SectionState) => {
    switch (state) {
      case "complete":
        return theme.success;
      case "attention":
        return theme.danger;
      case "in-progress":
        return theme.warning;
      default:
        return theme.textSecondary;
    }
  };

  const renderSectionStatusIcon = (
    state: SectionState,
    hasRedFlag: boolean,
    size = 16,
  ): React.JSX.Element => {
    if (hasRedFlag || state === "attention") {
      return (
        <LineIcon
          color={theme.danger}
          name="flag"
          size={size}
          strokeWidth={2.3}
        />
      );
    }
    if (state === "complete") {
      return (
        <LineIcon
          color={theme.success}
          name="check"
          size={size}
          strokeWidth={2.4}
        />
      );
    }
    if (state === "in-progress") {
      return (
        <LineIcon
          color={theme.warning}
          name="clock"
          size={size}
          strokeWidth={2.2}
        />
      );
    }
    return (
      <View
        style={{
          borderColor: theme.textTertiary,
          borderRadius: size / 2,
          borderWidth: 1.5,
          height: size,
          width: size,
        }}
      />
    );
  };

  const stateChip = (state: SectionState) => {
    switch (state) {
      case "complete":
        return theme.statusChip.success;
      case "attention":
        return theme.statusChip.danger;
      case "in-progress":
        return theme.statusChip.warning;
      default:
        return null;
    }
  };

  // Plain language, no field paths and no error codes: this is read mid-chart
  // by a nurse, not in a log by an engineer.
  const saveIssueText = (issue: QuestionnaireSaveIssue): string => {
    switch (issue.reason) {
      case "invalid-value":
        return issue.sharedFieldPath
          ? "One of the answers sharing this field could not be saved — check them together."
          : "This answer could not be saved — please check it.";
      case "no-binding":
        return "This answer has nowhere to be filed yet, so it was not saved.";
      case "unreadable-answer":
        return "We could not read this answer — please re-enter it.";
      case "clear-refused":
        return "This answer was recorded elsewhere and cannot be cleared here.";
    }
  };

  const renderQuestion = (
    question: TenantAdminOutboundQuestionnaireQuestionV1,
    depth: number,
  ) => {
    if (!visibleSet.has(question.questionKey)) return null;
    const value = answers[question.questionKey];
    const promptText = question.promptText ?? question.questionKey;
    const promptUnavailable = unavailableQuestionKeys.has(question.questionKey);
    const label = promptUnavailable
      ? resolveQuestionnairePromptText(promptText, record)
      : outboundSession != null
        ? resolveQuestionnaireReviewPromptText(
            question.questionKey,
            promptText,
          )
        : resolveQuestionnairePromptText(promptText, record);
    const missing =
      question.required && evaluation.requiredMissingKeys.includes(question.questionKey);
    const answered = canonicalAnswers[question.questionKey] !== undefined;
    const flagSignal = answerReviewSignal(
      question.redFlagRules,
      canonicalAnswers[question.questionKey],
    );
    const flagColor =
      flagSignal === "red_flag"
        ? theme.danger
        : flagSignal === "yellow_flag"
          ? theme.warning
          : null;

    const rowStyle = {
      marginBottom: workforceConfigurationDialog.fieldGap,
      marginLeft: Math.min(depth, 2) * (isCompactSurface ? 10 : 18),
      padding: isCompactSurface
        ? workforceConfigurationDialog.paddingCompact
        : workforceConfigurationDialog.paddingWide,
      backgroundColor: depth > 0 ? theme.surfaceMuted : theme.surface,
      borderRadius: workforceOperationalLayout.panelRadius,
      borderWidth: 1,
      borderColor: theme.borderSubtle,
      borderLeftWidth: 3,
      borderLeftColor: flagColor ?? (answered ? theme.accentStrong : theme.borderSubtle),
    } as const;

    // Dropped as soon as the nurse edits the answer: a marker that outlives the
    // fix it asked for reads as "still broken" on a question already corrected.
    const rawSaveIssue = activeSectionIssueByQuestionKey.get(
      question.questionKey,
    );
    const saveIssue =
      rawSaveIssue != null &&
      rawSaveIssue.answerSnapshot ===
        encodeQuestionnaireAnswerSnapshot(
          canonicalAnswers[question.questionKey],
        )
        ? rawSaveIssue
        : undefined;

    // Only choice questions carry optionsProvenance: the claim is about a list
    // of choices, and a free-text question has no list to be unconfirmed about.
    const optionsUnconfirmed =
      questionnaireOptionsAreUnconfirmed(question);

    const phoneProvenance = prefill.provenance[question.questionKey];
    const phoneEdited =
      phoneProvenance != null &&
      JSON.stringify(canonicalAnswers[question.questionKey]) !==
        JSON.stringify(prefillCanonicalValues[question.questionKey]);
    const labelRow = (
      <View style={{ gap: workforceConfigurationDialog.fieldLabelGap }}>
        <View
          style={{
            alignItems: "flex-start",
            flexDirection: "row",
            gap: 8,
          }}
        >
          <Text
            style={{
              color: theme.textPrimary,
              flex: 1,
              fontFamily: layout.bodyFontFamily,
              fontSize: workforceOperationalTypography.body.fontSize,
              fontWeight: "600",
              lineHeight: workforceOperationalTypography.body.lineHeight,
            }}
          >
            {label}
            {missing ? <Text style={{ color: theme.danger }}> *</Text> : null}
          </Text>
          {flagColor ? (
            <LineIcon
              color={flagColor}
              name="flag"
              size={17}
              strokeWidth={2.3}
            />
          ) : null}
        </View>
        {phoneProvenance ? (
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              gap: 6,
              marginBottom: 2,
            }}
          >
            <LineIcon
              color={phoneEdited ? theme.warning : theme.accentStrong}
              name="phone"
              size={14}
              strokeWidth={2.2}
            />
            <Text
              style={{
                color: phoneEdited ? theme.warning : theme.accentStrong,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {phoneEdited
                ? "Phone answer edited"
                : `Answered by phone${formatPrefillDate(phoneProvenance.answeredAt)}`}
            </Text>
          </View>
        ) : null}
        {optionsUnconfirmed ? (
          // Deliberately NOT an alert, and deliberately neither the "flag"
          // glyph (a red/yellow clinical review signal on this answer) nor the
          // "x" (this answer could not be saved). This says nothing about the
          // patient or their answer: it is a caveat about the QUESTION, namely
          // that we authored these choices and VIP has not confirmed them.
          // theme.danger/warning would read as "act on this patient".
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              gap: 6,
              marginBottom: 2,
            }}
          >
            <LineIcon
              color={theme.textSecondary}
              name="info"
              size={14}
              strokeWidth={2.2}
            />
            <Text
              style={{
                color: theme.textSecondary,
                flex: 1,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              These choices are not confirmed by VIP yet
            </Text>
          </View>
        ) : null}
        {saveIssue ? (
          <View
            accessibilityRole="alert"
            style={{
              alignItems: "center",
              flexDirection: "row",
              gap: 6,
              marginBottom: 2,
            }}
          >
            <LineIcon
              color={theme.danger}
              name="x"
              size={14}
              strokeWidth={2.2}
            />
            <Text
              style={{
                color: theme.danger,
                flex: 1,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {saveIssueText(saveIssue)}
            </Text>
          </View>
        ) : null}
      </View>
    );

    if (promptUnavailable) {
      return (
        <View key={question.questionKey} style={rowStyle}>
          {labelRow}
          <View
            accessibilityRole="alert"
            style={{
              alignItems: "flex-start",
              backgroundColor: theme.statusChip.warning.background,
              borderColor: theme.statusChip.warning.border,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              flexDirection: "row",
              gap: 8,
              marginTop: workforceConfigurationDialog.fieldLabelGap,
              paddingHorizontal: 12,
              paddingVertical: 10,
            }}
          >
            <LineIcon
              color={theme.statusChip.warning.text}
              name="info"
              size={16}
              strokeWidth={2.3}
            />
            <Text
              style={{
                color: theme.statusChip.warning.text,
                flex: 1,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              Add this value in Patient details before recording a confirmation.
            </Text>
          </View>
        </View>
      );
    }

    if (question.answerType === "yes_no" || question.answerType === "confirmation") {
      return (
        <View key={question.questionKey} style={rowStyle}>
          {labelRow}
          <View
            accessibilityLabel={`${label} answer choices`}
            role="radiogroup"
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: workforceConfigurationDialog.actionGap,
              marginTop: workforceConfigurationDialog.fieldLabelGap,
            }}
          >
            {[
              {
                text: question.answerType === "confirmation" ? "Confirmed" : "Yes",
                answer: true,
              },
              { text: "No", answer: false },
            ].map((option) => {
              const selected = value === option.answer;
              return (
                <Pressable
                  key={option.text}
                  accessibilityLabel={`${label}: ${option.text}`}
                  accessibilityRole="radio"
                  accessibilityState={{
                    checked: selected,
                    disabled: formBusy,
                  }}
                  aria-checked={selected}
                  aria-disabled={formBusy}
                  disabled={formBusy}
                  onPress={() => setAnswer(question.questionKey, option.answer)}
                  style={{
                    alignItems: "center",
                    borderWidth: 1,
                    borderColor: selected ? theme.accentStrong : theme.borderStrong,
                    backgroundColor: selected ? theme.accentSubtle : "transparent",
                    borderRadius: workforceOperationalLayout.controlRadius,
                    flexDirection: "row",
                    gap: 8,
                    justifyContent: "center",
                    minHeight: controlHeight,
                    minWidth: isCompactSurface ? 112 : 128,
                    opacity: formBusy ? 0.6 : 1,
                    paddingHorizontal: 14,
                  }}
                >
                  <View
                    style={{
                      alignItems: "center",
                      backgroundColor: selected
                        ? theme.accentStrong
                        : "transparent",
                      borderColor: selected
                        ? theme.accentStrong
                        : theme.borderStrong,
                      borderRadius: 9,
                      borderWidth: 1.5,
                      height: 18,
                      justifyContent: "center",
                      width: 18,
                    }}
                  >
                    {selected ? (
                      <View
                        style={{
                          backgroundColor: theme.accentContrast,
                          borderRadius: 4,
                          height: 8,
                          width: 8,
                        }}
                      />
                    ) : null}
                  </View>
                  <Text
                    style={{
                      color: selected ? theme.accentStrong : theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.body.fontSize,
                      fontWeight: selected ? "700" : "400",
                    }}
                  >
                    {option.text}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      );
    }

    if (question.answerType === "single_choice" || question.answerType === "multi_choice") {
      const normalization = parseAnswerNormalizationV1(question.answerNormalization);
      const selected: QuestionnaireSelectValue[] =
        hydrateQuestionnaireSelectValues(value, normalization.options);
      return (
        <View key={question.questionKey} style={rowStyle}>
          {labelRow}
          <SemanticQuestionnaireSelect
            controlHeight={controlHeight}
            disabled={formBusy}
            fontFamily={layout.bodyFontFamily}
            label={label}
            options={normalization.options}
            selected={selected}
            multi={question.answerType === "multi_choice"}
            canAddUnlisted
            theme={theme}
            onChange={(next) => setAnswer(question.questionKey, next)}
          />
        </View>
      );
    }

    if (question.answerType === "date") {
      return (
        <View key={question.questionKey} style={rowStyle}>
          {labelRow}
          <ResponsiveQuestionnaireDateField
            controlHeight={controlHeight}
            disabled={formBusy}
            fontFamily={layout.bodyFontFamily}
            value={typeof value === "string" ? value : value == null ? "" : String(value)}
            label={label}
            theme={theme}
            onDirtyChange={(dirty) =>
              setQuestionDraftDirty(question.questionKey, dirty)
            }
            onCommit={(text) => setAnswer(question.questionKey, text)}
          />
        </View>
      );
    }

    return (
      <View key={question.questionKey} style={rowStyle}>
        {labelRow}
        <TextAnswerRow
          value={typeof value === "string" ? value : value == null ? "" : String(value)}
          numeric={question.answerType === "number"}
          disabled={formBusy}
          label={label}
          theme={theme}
          controlHeight={controlHeight}
          fontFamily={layout.bodyFontFamily}
          onDirtyChange={(dirty) =>
            setQuestionDraftDirty(question.questionKey, dirty)
          }
          onCommit={(text) => setAnswer(question.questionKey, text)}
        />
      </View>
    );
  };

  // Follow-ups render directly under their gate, indented one level.
  const renderSectionQuestions = (section: SectionView) => {
    const rendered: React.JSX.Element[] = [];
    const childrenByParent = new Map<string, TenantAdminOutboundQuestionnaireQuestionV1[]>();
    const roots: TenantAdminOutboundQuestionnaireQuestionV1[] = [];

    for (const question of section.questions) {
      const parentKey = question.skipLogic?.onlyIf?.questionKey;
      const parentInSection =
        parentKey && section.questions.some((entry) => entry.questionKey === parentKey);
      if (parentInSection) {
        const list = childrenByParent.get(parentKey) ?? [];
        list.push(question);
        childrenByParent.set(parentKey, list);
      } else {
        roots.push(question);
      }
    }

    // Guard against a cyclic or self-referencing gate in a future template —
    // an unguarded recursion here would hang the workbench.
    const seen = new Set<string>();
    const push = (question: TenantAdminOutboundQuestionnaireQuestionV1, depth: number) => {
      if (seen.has(question.questionKey) || depth > 8) return;
      seen.add(question.questionKey);
      const element = renderQuestion(question, depth);
      if (element) rendered.push(element);
      for (const child of childrenByParent.get(question.questionKey) ?? []) {
        push(child, depth + 1);
      }
    };
    for (const root of roots) push(root, 0);

    return rendered;
  };

  const progressRatio =
    sections.length > 0 ? completeCount / sections.length : 0;
  const sectionKeys = sections.map((section) => section.key);
  const activeStat = activeSection
    ? sectionStats.get(activeSection.key)
    : undefined;
  const activeSectionIndex = activeSection
    ? sections.findIndex((section) => section.key === activeSection.key)
    : -1;
  const resolvedPatientIdentityText =
    patientIdentityText ?? "Patient identifiers unavailable";
  const closeBusy = formBusy;

  const renderSectionTab = (
    section: SectionView,
    compact: boolean,
    sectionIndex: number,
  ): React.JSX.Element => {
    const stat = sectionStats.get(section.key);
    const state = stat?.state ?? "untouched";
    const isActive = section.key === activeKey;
    const chip = stateChip(state);
    const statusText =
      state === "attention"
        ? `${sectionIssues[section.key] ?? 0} to fix`
        : state === "complete"
          ? stat?.saved
            ? "Saved"
            : "All answered"
          : state === "in-progress"
            ? `${stat?.answered ?? 0} answered`
            : "Not started";

    return (
      <Pressable
        key={section.key}
        accessibilityLabel={`${section.label}, ${statusText}`}
        accessibilityRole="tab"
        accessibilityState={{ selected: isActive }}
        aria-selected={isActive}
        onPress={() => setActiveSectionKey(section.key)}
        tabIndex={isActive ? 0 : -1}
        {...(Platform.OS === "web"
          ? {
              onKeyDown: (event: unknown) =>
                handleQuestionnaireTabKeyDown(
                  event as QuestionnaireTabKeyDownEvent,
                  sectionIndex,
                  sectionKeys,
                  setActiveSectionKey,
                ),
            }
          : {})}
        style={{
          alignItems: "center",
          backgroundColor: isActive ? theme.surface : "transparent",
          borderBottomWidth: compact ? 3 : 0,
          borderColor: isActive ? theme.accentStrong : "transparent",
          borderLeftWidth: compact ? 0 : 3,
          borderRadius: compact
            ? workforceOperationalLayout.controlRadius
            : 0,
          flexDirection: railCollapsed && !compact ? "column" : "row",
          gap: railCollapsed && !compact ? 0 : 9,
          justifyContent:
            railCollapsed && !compact ? "center" : "flex-start",
          minHeight: compact
            ? workforceOperationalLayout.mobileControlHeight
            : controlHeight,
          minWidth: compact ? 164 : undefined,
          paddingHorizontal: railCollapsed && !compact ? 0 : 12,
          paddingVertical: compact ? 7 : 8,
          width: compact ? 184 : "100%",
        }}
      >
        <View
          style={{
            alignItems: "center",
            flexShrink: 0,
            height: 18,
            justifyContent: "center",
            width: 18,
          }}
        >
          {renderSectionStatusIcon(state, stat?.redFlag === true, 16)}
        </View>
        {railCollapsed && !compact ? null : (
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              numberOfLines={compact ? 1 : 2}
              style={{
                color: isActive ? theme.textPrimary : theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.table.fontSize,
                fontWeight: isActive ? "700" : "600",
                lineHeight: workforceOperationalTypography.table.lineHeight,
              }}
            >
              {section.label}
            </Text>
            <Text
              style={{
                color: chip?.text ?? stateColor(state),
                fontFamily: layout.bodyFontFamily,
                fontSize: workforceOperationalTypography.metadata.fontSize,
                lineHeight: workforceOperationalTypography.metadata.lineHeight,
              }}
            >
              {statusText}
            </Text>
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <Modal
      accessibilityLabel={
        showCloseConfirmation
          ? "Leave questionnaire without saving"
          : "Patient questionnaire"
      }
      accessibilityViewIsModal
      animationType="slide"
      onRequestClose={
        showCloseConfirmation
          ? () => setShowCloseConfirmation(false)
          : requestClose
      }
      visible={visible}
    >
      <View style={{ backgroundColor: theme.background, flex: 1 }}>
        <View
          accessibilityElementsHidden={showCloseConfirmation}
          aria-hidden={showCloseConfirmation}
          importantForAccessibility={
            showCloseConfirmation ? "no-hide-descendants" : "auto"
          }
          pointerEvents={showCloseConfirmation ? "none" : "auto"}
          style={{ flex: 1 }}
        >
        <View
          style={{
            backgroundColor: theme.surface,
            borderBottomColor: theme.borderSubtle,
            borderBottomWidth: 1,
            paddingHorizontal: contentPadding,
            paddingVertical: isCompactSurface ? 10 : 12,
          }}
        >
          <View
            style={{
              alignItems: isCompactSurface ? "stretch" : "center",
              flexDirection: isCompactSurface ? "column" : "row",
              gap: isCompactSurface ? 10 : 18,
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                alignItems: "flex-start",
                flex: 1,
                flexDirection: "row",
                minWidth: 0,
              }}
            >
              <Pressable
                accessibilityLabel="Back to patient"
                accessibilityRole="button"
                onPress={requestClose}
                style={{
                  alignItems: "center",
                  borderColor: theme.borderStrong,
                  borderRadius: workforceOperationalLayout.controlRadius,
                  borderWidth: 1,
                  flexDirection: "row",
                  gap: 4,
                  justifyContent: "center",
                  minHeight: controlHeight,
                  paddingHorizontal: isCompactSurface ? 8 : 11,
                }}
              >
                <View style={{ transform: [{ rotate: "180deg" }] }}>
                  <LineIcon
                    color={theme.accentStrong}
                    name="chevron-right"
                    size={17}
                    strokeWidth={2.3}
                  />
                </View>
                <Text
                  style={{
                    color: theme.accentStrong,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    fontWeight: "700",
                  }}
                >
                  Back
                </Text>
              </Pressable>
              <View
                style={{
                  flex: 1,
                  gap: 2,
                  marginLeft: isCompactSurface ? 10 : 14,
                  minWidth: 0,
                }}
              >
                <Text
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: isCompactSurface
                      ? 20
                      : workforceOperationalTypography.pageTitle.fontSize,
                    fontWeight: "700",
                    lineHeight: isCompactSurface
                      ? 25
                      : workforceOperationalTypography.pageTitle.lineHeight,
                  }}
                >
                  Pre-op questionnaire
                </Text>
                <Text
                  style={{
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    fontWeight: "700",
                    lineHeight: workforceOperationalTypography.body.lineHeight,
                  }}
                >
                  {patientDisplayName}
                </Text>
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.metadata.fontSize,
                    lineHeight: workforceOperationalTypography.metadata.lineHeight,
                  }}
                >
                  {resolvedPatientIdentityText}
                </Text>
              </View>
            </View>

            <View
              style={{
                gap: 6,
                maxWidth: isCompactSurface ? undefined : 390,
                minWidth: isCompactSurface ? undefined : 320,
                width: isCompactSurface ? "100%" : undefined,
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  justifyContent: "space-between",
                }}
              >
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.table.fontSize,
                    lineHeight: workforceOperationalTypography.table.lineHeight,
                  }}
                >
                  <Text
                    style={{
                      color:
                        completeCount > 0
                          ? theme.success
                          : theme.textSecondary,
                      fontWeight: "700",
                    }}
                  >
                    {completeCount} of {sections.length} sections saved
                  </Text>
                  {evaluation.answeredVisibleCount > 0
                    ? ` · ${evaluation.answeredVisibleCount} ${
                        evaluation.answeredVisibleCount === 1
                          ? "answer"
                          : "answers"
                      } entered`
                    : ""}
                </Text>
                <View
                  style={{
                    alignItems: "center",
                    flexDirection: "row",
                    gap: 8,
                  }}
                >
                  {dirtyCount > 0 ? (
                    <View
                      style={{
                        backgroundColor:
                          theme.statusChip.warning.background,
                        borderColor: theme.statusChip.warning.border,
                        borderRadius: workforceOperationalLayout.controlRadius,
                        borderWidth: 1,
                        paddingHorizontal: 8,
                        paddingVertical: 3,
                      }}
                    >
                      <Text
                        style={{
                          color: theme.statusChip.warning.text,
                          fontFamily: layout.bodyFontFamily,
                          fontSize:
                            workforceOperationalTypography.metadata.fontSize,
                          fontWeight: "700",
                          lineHeight:
                            workforceOperationalTypography.metadata.lineHeight,
                        }}
                      >
                        {dirtyCount} unsaved{" "}
                        {dirtyCount === 1 ? "section" : "sections"}
                      </Text>
                    </View>
                  ) : null}
                  {!isCompactSurface ? (
                    <Pressable
                      accessibilityLabel={
                        railCollapsed
                          ? "Expand section list"
                          : "Collapse section list"
                      }
                      accessibilityRole="button"
                      accessibilityState={{ expanded: !railCollapsed }}
                      aria-expanded={!railCollapsed}
                      onPress={() =>
                        setRailCollapsed((current) => !current)
                      }
                      style={{
                        alignItems: "center",
                        borderColor: theme.borderStrong,
                        borderRadius:
                          workforceOperationalLayout.controlRadius,
                        borderWidth: 1,
                        flexDirection: "row",
                        gap: 6,
                        justifyContent: "center",
                        minHeight: controlHeight,
                        paddingHorizontal: 10,
                      }}
                    >
                      <View
                        style={{
                          transform: [
                            {
                              rotate: railCollapsed ? "0deg" : "180deg",
                            },
                          ],
                        }}
                      >
                        <LineIcon
                          color={theme.textSecondary}
                          name="chevron-right"
                          size={16}
                          strokeWidth={2.2}
                        />
                      </View>
                      <Text
                        style={{
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize:
                            workforceOperationalTypography.table.fontSize,
                          fontWeight: "700",
                        }}
                      >
                        Sections
                      </Text>
                    </Pressable>
                  ) : null}
                </View>
              </View>
              <View
                accessibilityLabel={`${completeCount} of ${sections.length} sections saved`}
                accessibilityRole="progressbar"
                accessibilityValue={{
                  max: sections.length,
                  min: 0,
                  now: completeCount,
                }}
                aria-valuemax={sections.length}
                aria-valuemin={0}
                aria-valuenow={completeCount}
                style={{
                  backgroundColor: theme.surfaceMuted,
                  borderRadius: 3,
                  height: 6,
                  overflow: "hidden",
                }}
              >
                <View
                  style={{
                    backgroundColor: theme.accentStrong,
                    height: 6,
                    width: `${Math.round(progressRatio * 100)}%`,
                  }}
                />
              </View>
            </View>
          </View>

          {/* Also renders without a session: a manual-entry patient still needs
              somewhere to see that a save was rejected. */}
          {outboundSession != null || saveErrorText != null ? (
            <View
              style={{
                alignItems: "flex-start",
                backgroundColor: saveErrorText
                  ? theme.statusChip.danger.background
                  : completionNotice
                    ? theme.statusChip.success.background
                    : theme.accentSubtle,
                borderColor: saveErrorText
                  ? theme.statusChip.danger.border
                  : completionNotice
                    ? theme.statusChip.success.border
                    : theme.accentStrong,
                borderRadius: workforceOperationalLayout.panelRadius,
                borderWidth: 1,
                flexDirection: "row",
                gap: 8,
                marginTop: 10,
                paddingHorizontal: 10,
                paddingVertical: 8,
              }}
            >
              <LineIcon
                color={
                  saveErrorText
                    ? theme.statusChip.danger.text
                    : completionNotice
                      ? theme.statusChip.success.text
                      : theme.accentStrong
                }
                name={
                  saveErrorText
                    ? "flag"
                    : completionNotice
                      ? "check"
                      : "phone"
                }
                size={16}
                strokeWidth={2.3}
              />
              <Text
                style={{
                  color: saveErrorText
                    ? theme.statusChip.danger.text
                    : completionNotice
                      ? theme.statusChip.success.text
                      : theme.accentStrong,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.table.fontSize,
                  lineHeight: workforceOperationalTypography.table.lineHeight,
                }}
              >
                {saveErrorText ??
                  completionNotice ??
                  (outboundSession != null
                    ? `${outboundSession.answeredQuestionCount} of ${outboundSession.totalQuestionCount} questions were answered by the AI call. Those answers are prefilled below. Completing manually stops automated calls for this round.`
                    : null)}
              </Text>
            </View>
          ) : null}
        </View>

        {isCompactSurface ? (
          <ScrollView
            accessibilityLabel="Questionnaire sections"
            contentContainerStyle={{
              gap: 8,
              paddingHorizontal: 10,
              paddingVertical: 8,
            }}
            horizontal
            role="tablist"
            showsHorizontalScrollIndicator={false}
            style={{
              backgroundColor: theme.surfaceMuted,
              borderBottomColor: theme.borderSubtle,
              borderBottomWidth: 1,
              flexGrow: 0,
              flexShrink: 0,
            }}
          >
            {sections.map((section, sectionIndex) =>
              renderSectionTab(section, true, sectionIndex),
            )}
          </ScrollView>
        ) : null}

        <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
          {!isCompactSurface ? (
            <ScrollView
              accessibilityLabel="Questionnaire sections"
              contentContainerStyle={{ paddingVertical: 8 }}
              role="tablist"
              style={{
                backgroundColor: theme.surfaceMuted,
                borderRightColor: theme.borderSubtle,
                borderRightWidth: 1,
                flexBasis: railCollapsed ? 52 : expandedRailWidth,
                flexGrow: 0,
                flexShrink: 0,
                width: railCollapsed ? 52 : expandedRailWidth,
              }}
            >
              {sections.map((section, sectionIndex) =>
                renderSectionTab(section, false, sectionIndex),
              )}
            </ScrollView>
          ) : null}

          <View style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
            {activeSection ? (
              <>
                <View
                  style={{
                    backgroundColor: theme.surfaceMuted,
                    borderBottomColor: theme.borderSubtle,
                    borderBottomWidth: 1,
                  }}
                >
                  <View
                    style={{
                      alignSelf: "center",
                      paddingHorizontal: contentPadding,
                      paddingVertical: 10,
                      width: "100%",
                      maxWidth: workforceConfigurationDialog.maxFormWidth,
                    }}
                  >
                    <View
                      style={{
                        alignItems: "center",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <Text
                        style={{
                          color: theme.textPrimary,
                          flexShrink: 1,
                          fontFamily: layout.displayFontFamily,
                          fontSize:
                            workforceOperationalTypography.sectionTitle
                              .fontSize,
                          fontWeight: "700",
                          lineHeight:
                            workforceOperationalTypography.sectionTitle
                              .lineHeight,
                        }}
                      >
                        {activeSection.label}
                      </Text>
                      {activeStat?.state === "complete" ? (
                        <View
                          style={{
                            alignItems: "center",
                            flexDirection: "row",
                            gap: 5,
                          }}
                        >
                          <LineIcon
                            color={theme.success}
                            name="check"
                            size={16}
                            strokeWidth={2.4}
                          />
                          <Text
                            style={{
                              color: theme.success,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.table.fontSize,
                              fontWeight: "700",
                            }}
                          >
                            {activeStat.saved ? "Saved" : "All answered"}
                          </Text>
                        </View>
                      ) : null}
                    </View>
                    <Text
                      style={{
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize:
                          workforceOperationalTypography.metadata.fontSize,
                        lineHeight:
                          workforceOperationalTypography.metadata.lineHeight,
                        marginTop: 2,
                      }}
                    >
                      Section {activeSectionIndex + 1} of {sections.length}
                      {activeStat && activeStat.answered > 0
                        ? ` · ${activeStat.answered} ${
                            activeStat.answered === 1 ? "answer" : "answers"
                          } entered`
                        : " · No answers entered"}
                    </Text>
                  </View>
                </View>

                <ScrollView
                  contentContainerStyle={{
                    alignItems: "center",
                    paddingBottom: 24,
                    paddingHorizontal: contentPadding,
                    paddingTop: 14,
                  }}
                  key={`${activeSection.key}:${formRevision}`}
                  keyboardShouldPersistTaps="handled"
                  style={{ flex: 1 }}
                >
                  <View
                    style={{
                      maxWidth: workforceConfigurationDialog.maxFormWidth,
                      width: "100%",
                    }}
                  >
                    {activeStat?.total === 0 ? (
                      <View
                        style={{
                          alignItems: "center",
                          backgroundColor: theme.surface,
                          borderColor: theme.borderSubtle,
                          borderRadius: workforceOperationalLayout.panelRadius,
                          borderWidth: 1,
                          padding: contentPadding,
                        }}
                      >
                        <Text
                          style={{
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.body.fontSize,
                            lineHeight:
                              workforceOperationalTypography.body.lineHeight,
                            textAlign: "center",
                          }}
                        >
                          No questions are currently available in this section.
                        </Text>
                      </View>
                    ) : (
                      renderSectionQuestions(activeSection)
                    )}
                  </View>
                </ScrollView>

                <View
                  style={{
                    backgroundColor: theme.surface,
                    borderTopColor: theme.borderSubtle,
                    borderTopWidth: 1,
                  }}
                >
                  <View
                    style={{
                      alignSelf: "center",
                      gap: 8,
                      maxWidth: workforceConfigurationDialog.maxFormWidth,
                      paddingHorizontal: contentPadding,
                      paddingVertical: 10,
                      width: "100%",
                    }}
                  >
                    {outboundSession != null &&
                    completionNotice == null ? (
                      completeArmed ? (
                        <View
                          style={{
                            alignItems: "center",
                            backgroundColor:
                              theme.statusChip.warning.background,
                            borderColor:
                              theme.statusChip.warning.border,
                            borderRadius:
                              workforceOperationalLayout.panelRadius,
                            borderWidth: 1,
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 8,
                            padding: 10,
                          }}
                        >
                          <Text
                            style={{
                              color: theme.statusChip.warning.text,
                              flex: 1,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.table.fontSize,
                              fontWeight: "600",
                              lineHeight:
                                workforceOperationalTypography.table
                                  .lineHeight,
                              minWidth: isCompactSurface ? "100%" : 230,
                            }}
                          >
                            All entered answers are saved. Stop automated calls
                            and close this round?
                          </Text>
                          <Pressable
                            accessibilityLabel="Confirm manual completion"
                            accessibilityRole="button"
                            accessibilityState={{
                              disabled: !canCompleteManually,
                            }}
                            aria-disabled={!canCompleteManually}
                            disabled={!canCompleteManually}
                            onPress={completeManually}
                            style={{
                              alignItems: "center",
                              backgroundColor: theme.success,
                              borderRadius:
                                workforceOperationalLayout.controlRadius,
                              flexDirection: "row",
                              gap: 6,
                              justifyContent: "center",
                              minHeight: controlHeight,
                              opacity: canCompleteManually ? 1 : 0.5,
                              paddingHorizontal: 12,
                            }}
                          >
                            <LineIcon
                              color={theme.accentContrast}
                              name="check"
                              size={16}
                              strokeWidth={2.4}
                            />
                            <Text
                              style={{
                                color: theme.accentContrast,
                                fontFamily: layout.bodyFontFamily,
                                fontSize:
                                  workforceOperationalTypography.table
                                    .fontSize,
                                fontWeight: "700",
                              }}
                            >
                              {isOutboundCommandSubmitting
                                ? "Completing..."
                                : "Confirm and stop calls"}
                            </Text>
                          </Pressable>
                          <Pressable
                            accessibilityLabel="Keep automated calling"
                            accessibilityRole="button"
                            onPress={() => setCompleteArmed(false)}
                            style={{
                              alignItems: "center",
                              borderColor: theme.borderStrong,
                              borderRadius:
                                workforceOperationalLayout.controlRadius,
                              borderWidth: 1,
                              justifyContent: "center",
                              minHeight: controlHeight,
                              paddingHorizontal: 12,
                            }}
                          >
                            <Text
                              style={{
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize:
                                  workforceOperationalTypography.table
                                    .fontSize,
                                fontWeight: "700",
                              }}
                            >
                              Keep calling
                            </Text>
                          </Pressable>
                        </View>
                      ) : (
                        <Pressable
                          accessibilityLabel="Complete questionnaire manually"
                          accessibilityHint={
                            manualCompletionBlockText ??
                            "Stops automated calls after a confirmation."
                          }
                          accessibilityRole="button"
                          accessibilityState={{
                            disabled: !canCompleteManually,
                          }}
                          aria-disabled={!canCompleteManually}
                          disabled={!canCompleteManually}
                          onPress={() => setCompleteArmed(true)}
                          style={{
                            alignItems: "center",
                            alignSelf: "flex-start",
                            borderColor: canCompleteManually
                              ? theme.success
                              : theme.borderStrong,
                            borderRadius:
                              workforceOperationalLayout.controlRadius,
                            borderWidth: 1,
                            flexDirection: "row",
                            gap: 6,
                            justifyContent: "center",
                            minHeight: controlHeight,
                            opacity: canCompleteManually ? 1 : 0.55,
                            paddingHorizontal: 12,
                          }}
                        >
                          <LineIcon
                            color={
                              canCompleteManually
                                ? theme.success
                                : theme.textSecondary
                            }
                            name="check"
                            size={16}
                            strokeWidth={2.4}
                          />
                          <Text
                            style={{
                              color: canCompleteManually
                                ? theme.success
                                : theme.textSecondary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize:
                                workforceOperationalTypography.table.fontSize,
                              fontWeight: "700",
                            }}
                          >
                            Complete manually
                          </Text>
                        </Pressable>
                      )
                    ) : null}

                    {outboundSession != null &&
                    completionNotice == null &&
                    manualCompletionBlockText != null ? (
                      <View
                        accessibilityLiveRegion="polite"
                        style={{
                          alignItems: "center",
                          flexDirection: "row",
                          gap: 7,
                        }}
                      >
                        <LineIcon
                          color={theme.textSecondary}
                          name="info"
                          size={15}
                          strokeWidth={2.2}
                        />
                        <Text
                          style={{
                            color: theme.textSecondary,
                            flex: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.metadata.fontSize,
                            lineHeight:
                              workforceOperationalTypography.metadata
                                .lineHeight,
                          }}
                        >
                          {manualCompletionBlockText}
                        </Text>
                      </View>
                    ) : null}

                    {activeSectionIssues.length > 0 ? (
                      <View
                        style={{
                          alignItems: "center",
                          flexDirection: "row",
                          gap: 6,
                        }}
                      >
                        {/* Deliberately NOT the flag glyph: a flag already means
                            a red/yellow clinical review signal on this screen,
                            and a save failure is a different thing. */}
                        <LineIcon
                          color={theme.danger}
                          name="x"
                          size={16}
                          strokeWidth={2.3}
                        />
                        <Text
                          style={{
                            color: theme.danger,
                            flex: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.table.fontSize,
                            lineHeight:
                              workforceOperationalTypography.table.lineHeight,
                          }}
                        >
                          {activeSectionIssues.length}{" "}
                          {activeSectionIssues.length === 1
                            ? "answer could not be saved"
                            : "answers could not be saved"}
                          {" — marked below"}
                        </Text>
                      </View>
                    ) : null}

                    <View
                      style={{
                        alignItems: "center",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: workforceConfigurationDialog.actionGap,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Pressable
                        accessibilityLabel="Save section"
                        accessibilityRole="button"
                        accessibilityState={{ disabled: formBusy }}
                        aria-disabled={formBusy}
                        disabled={formBusy}
                        onPress={() => saveSection(activeSection, false)}
                        style={{
                          alignItems: "center",
                          borderColor: theme.borderStrong,
                          borderRadius:
                            workforceOperationalLayout.controlRadius,
                          borderWidth: 1,
                          flexGrow: isCompactSurface ? 1 : 0,
                          justifyContent: "center",
                          minHeight: controlHeight,
                          minWidth: isCompactSurface ? 130 : 112,
                          opacity: formBusy ? 0.5 : 1,
                          paddingHorizontal: 14,
                        }}
                      >
                        <Text
                          style={{
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.body.fontSize,
                            fontWeight: "700",
                          }}
                        >
                          Save only
                        </Text>
                      </Pressable>
                      <Pressable
                        accessibilityLabel="Save section and continue"
                        accessibilityRole="button"
                        accessibilityState={{ disabled: formBusy }}
                        aria-disabled={formBusy}
                        disabled={formBusy}
                        onPress={() => saveSection(activeSection, true)}
                        style={{
                          alignItems: "center",
                          backgroundColor: theme.accentStrong,
                          borderRadius:
                            workforceOperationalLayout.controlRadius,
                          flexDirection: "row",
                          flexGrow: isCompactSurface ? 1 : 0,
                          gap: 6,
                          justifyContent: "center",
                          minHeight: controlHeight,
                          minWidth: isCompactSurface ? 180 : 190,
                          opacity: formBusy ? 0.5 : 1,
                          paddingHorizontal: 16,
                        }}
                      >
                        <Text
                          style={{
                            color: theme.accentContrast,
                            fontFamily: layout.bodyFontFamily,
                            fontSize:
                              workforceOperationalTypography.body.fontSize,
                            fontWeight: "700",
                          }}
                        >
                          {formBusy
                            ? "Saving..."
                            : "Save and next section"}
                        </Text>
                        {!formBusy ? (
                          <LineIcon
                            color={theme.accentContrast}
                            name="chevron-right"
                            size={16}
                            strokeWidth={2.3}
                          />
                        ) : null}
                      </Pressable>
                    </View>
                  </View>
                </View>
              </>
            ) : (
              <View
                style={{
                  alignItems: "center",
                  flex: 1,
                  justifyContent: "center",
                  padding: contentPadding,
                }}
              >
                <Text
                  style={{
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.body.fontSize,
                    lineHeight: workforceOperationalTypography.body.lineHeight,
                    textAlign: "center",
                  }}
                >
                  This questionnaire has no sections.
                </Text>
              </View>
            )}
          </View>
        </View>
        </View>

        {showCloseConfirmation ? (
          <View
            style={{
              alignItems: "center",
              backgroundColor: theme.backdropOverlay,
              bottom: 0,
              justifyContent: "center",
              left: 0,
              padding: 16,
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 100,
            }}
          >
            <View
              accessibilityLabel="Leave questionnaire without saving"
              ref={closeConfirmationPromptRef}
              style={{
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: workforceOperationalLayout.panelRadius,
                borderWidth: 1,
                elevation: 12,
                gap: 12,
                maxWidth: 440,
                padding: workforceConfigurationDialog.paddingWide,
                width: Math.min(
                  440,
                  Math.max(280, viewportWidth - 32),
                ),
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  flexDirection: "row",
                  gap: 8,
                }}
              >
                <LineIcon
                  color={theme.warning}
                  name="info"
                  size={20}
                  strokeWidth={2.3}
                />
                <Text
                  style={{
                    color: theme.textPrimary,
                    flex: 1,
                    fontFamily: layout.displayFontFamily,
                    fontSize:
                      workforceOperationalTypography.sectionTitle.fontSize,
                    fontWeight: "700",
                    lineHeight:
                      workforceOperationalTypography.sectionTitle.lineHeight,
                  }}
                >
                  Leave without saving?
                </Text>
              </View>
              <Text
                style={{
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: workforceOperationalTypography.body.fontSize,
                  lineHeight: workforceOperationalTypography.body.lineHeight,
                }}
              >
                {dirtyCount}{" "}
                {dirtyCount === 1 ? "section has" : "sections have"} unsaved
                changes. Leaving now discards those changes.
              </Text>
              {closeBusy ? (
                <Text
                  style={{
                    color: theme.warning,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: workforceOperationalTypography.table.fontSize,
                    lineHeight: workforceOperationalTypography.table.lineHeight,
                  }}
                >
                  A save is still in progress. Wait for it to finish before
                  leaving.
                </Text>
              ) : null}
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: workforceConfigurationDialog.actionGap,
                  justifyContent: "flex-end",
                }}
              >
                <Pressable
                  accessibilityLabel="Stay in questionnaire"
                  accessibilityRole="button"
                  onPress={() => setShowCloseConfirmation(false)}
                  ref={closeConfirmationStayButtonRef}
                  style={{
                    alignItems: "center",
                    borderColor: theme.borderStrong,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    borderWidth: 1,
                    flexGrow: isCompactSurface ? 1 : 0,
                    justifyContent: "center",
                    minHeight: controlHeight,
                    minWidth: 110,
                    paddingHorizontal: 14,
                  }}
                >
                  <Text
                    style={{
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.body.fontSize,
                      fontWeight: "700",
                    }}
                  >
                    Stay
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityLabel="Discard changes and leave"
                  accessibilityRole="button"
                  accessibilityState={{ disabled: closeBusy }}
                  aria-disabled={closeBusy}
                  disabled={closeBusy}
                  onPress={discardAndClose}
                  style={{
                    alignItems: "center",
                    backgroundColor: theme.danger,
                    borderRadius: workforceOperationalLayout.controlRadius,
                    flexGrow: isCompactSurface ? 1 : 0,
                    justifyContent: "center",
                    minHeight: controlHeight,
                    minWidth: 168,
                    opacity: closeBusy ? 0.5 : 1,
                    paddingHorizontal: 14,
                  }}
                >
                  <Text
                    style={{
                      color: theme.accentContrast,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: workforceOperationalTypography.body.fontSize,
                      fontWeight: "700",
                    }}
                  >
                    Discard and leave
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
        ) : null}
      </View>
    </Modal>
  );
}

interface SemanticQuestionnaireSelectProps {
  canAddUnlisted: boolean;
  controlHeight: number;
  disabled: boolean;
  fontFamily: string;
  label: string;
  multi: boolean;
  onChange: (selected: QuestionnaireSelectValue[]) => void;
  options: QuestionnaireSelectOption[];
  selected: QuestionnaireSelectValue[];
  theme: AppShellTheme;
}

function SemanticQuestionnaireSelect({
  canAddUnlisted,
  controlHeight,
  disabled,
  fontFamily,
  label,
  multi,
  onChange,
  options,
  selected,
  theme,
}: SemanticQuestionnaireSelectProps): React.JSX.Element {
  const [query, setQuery] = useState("");
  const matches = useMemo(
    () => filterQuestionnaireSelectOptions(query, options),
    [query, options],
  );
  const selectedValues = new Set(selected.map((entry) => entry.value));
  const selectedMatchIndex = matches.findIndex((option) =>
    selectedValues.has(option.value),
  );
  const rovingFocusIndex = resolveRovingFocusIndex(
    selectedMatchIndex,
    matches.length,
  );

  const toggle = (value: string, unlisted: boolean): void => {
    if (disabled) {
      return;
    }

    if (selectedValues.has(value)) {
      if (!multi) {
        return;
      }

      onChange(selected.filter((entry) => entry.value !== value));
      return;
    }
    const next = { unlisted, value };
    onChange(multi ? [...selected, next] : [next]);
    setQuery("");
  };

  const trimmed = query.trim();
  const showAddRow =
    canAddUnlisted &&
    trimmed.length > 0 &&
    !options.some(
      (option) => option.label.toLowerCase() === trimmed.toLowerCase(),
    );

  return (
    <View
      accessibilityLabel={`${label} options`}
      pointerEvents={disabled ? "none" : "auto"}
      style={{
        gap: workforceConfigurationDialog.fieldLabelGap,
        marginTop: workforceConfigurationDialog.fieldLabelGap,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <TextInput
        accessibilityLabel={`${label} search`}
        editable={!disabled}
        onChangeText={setQuery}
        placeholder="Type to search"
        placeholderTextColor={theme.textTertiary}
        style={{
          backgroundColor: theme.surface,
          borderColor: theme.borderStrong,
          borderRadius: workforceOperationalLayout.controlRadius,
          borderWidth: 1,
          color: theme.textPrimary,
          fontFamily,
          fontSize: workforceOperationalTypography.body.fontSize,
          minHeight: controlHeight,
          paddingHorizontal: 11,
          paddingVertical: 8,
        }}
        value={query}
      />

      {multi && selected.length > 0 ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          {selected.map((entry) => (
            <Pressable
              accessibilityLabel={`Remove ${entry.value}`}
              accessibilityRole="button"
              accessibilityState={{ disabled, selected: true }}
              aria-disabled={disabled}
              aria-pressed={true}
              disabled={disabled}
              key={entry.value}
              onPress={() => toggle(entry.value, entry.unlisted)}
              style={{
                alignItems: "center",
                backgroundColor: entry.unlisted
                  ? theme.statusChip.warning.background
                  : theme.accentSubtle,
                borderColor: entry.unlisted
                  ? theme.statusChip.warning.border
                  : theme.accentStrong,
                borderRadius: workforceOperationalLayout.controlRadius,
                borderWidth: 1,
                flexDirection: "row",
                gap: 6,
                minHeight: controlHeight,
                paddingHorizontal: 10,
              }}
            >
              <Text
                style={{
                  color: entry.unlisted
                    ? theme.statusChip.warning.text
                    : theme.accentStrong,
                  fontFamily,
                  fontSize: workforceOperationalTypography.table.fontSize,
                  fontWeight: "700",
                  lineHeight: workforceOperationalTypography.table.lineHeight,
                }}
              >
                {entry.value}
                {entry.unlisted ? " (unlisted)" : ""}
              </Text>
              <LineIcon
                color={
                  entry.unlisted
                    ? theme.statusChip.warning.text
                    : theme.accentStrong
                }
                name="x"
                size={14}
                strokeWidth={2.3}
              />
            </Pressable>
          ))}
        </View>
      ) : null}

      <View
        accessibilityLabel={multi ? undefined : `${label} choices`}
        role={multi ? undefined : "radiogroup"}
        style={{ gap: 4 }}
      >
        {matches.map((option, optionIndex) => {
          const isSelected = selectedValues.has(option.value);
          return (
            <Pressable
              accessibilityLabel={`${label}: ${option.label}`}
              accessibilityRole={multi ? "checkbox" : "radio"}
              accessibilityState={{
                checked: isSelected,
                disabled,
              }}
              aria-checked={isSelected}
              aria-disabled={disabled}
              disabled={disabled}
              key={option.value}
              onPress={() => toggle(option.value, false)}
              tabIndex={
                multi
                  ? 0
                  : optionIndex === rovingFocusIndex
                    ? 0
                    : -1
              }
              {...(Platform.OS === "web" && !multi
                ? {
                    onKeyDown: (event: unknown) => {
                      const keyboardEvent =
                        event as QuestionnaireTabKeyDownEvent;
                      const nextIndex =
                        resolveHorizontalTabNavigationIndex(
                          keyboardEvent.key ??
                            keyboardEvent.nativeEvent?.key ??
                            "",
                          optionIndex,
                          matches.length,
                        );
                      const nextOption =
                        nextIndex == null ? undefined : matches[nextIndex];

                      if (nextIndex == null || nextOption == null) {
                        return;
                      }

                      keyboardEvent.preventDefault?.();
                      toggle(nextOption.value, false);

                      const currentTarget =
                        keyboardEvent.currentTarget as
                          | HTMLElement
                          | null
                          | undefined;
                      const radioGroup =
                        currentTarget?.closest?.('[role="radiogroup"]');

                      setTimeout(() => {
                        radioGroup
                          ?.querySelector<HTMLElement>(
                            '[role="radio"][aria-checked="true"]',
                          )
                          ?.focus();
                      }, 0);
                    },
                  }
                : {})}
              style={{
                alignItems: "center",
                backgroundColor: isSelected
                  ? theme.accentSubtle
                  : "transparent",
                borderColor: isSelected
                  ? theme.accentStrong
                  : theme.borderSubtle,
                borderRadius: workforceOperationalLayout.controlRadius,
                borderWidth: 1,
                flexDirection: "row",
                gap: 10,
                minHeight: controlHeight,
                paddingHorizontal: 10,
                paddingVertical: 6,
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  backgroundColor: isSelected
                    ? theme.accentStrong
                    : "transparent",
                  borderColor: isSelected
                    ? theme.accentStrong
                    : theme.borderStrong,
                  borderRadius: multi ? 4 : 9,
                  borderWidth: 1.5,
                  height: 18,
                  justifyContent: "center",
                  width: 18,
                }}
              >
                {isSelected ? (
                  <LineIcon
                    color={theme.accentContrast}
                    name="check"
                    size={13}
                    strokeWidth={2.6}
                  />
                ) : null}
              </View>
              <Text
                style={{
                  color: theme.textPrimary,
                  flex: 1,
                  fontFamily,
                  fontSize: workforceOperationalTypography.body.fontSize,
                  lineHeight: workforceOperationalTypography.body.lineHeight,
                }}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
        {matches.length === 0 && !showAddRow ? (
          <Text
            style={{
              color: theme.textSecondary,
              fontFamily,
              fontSize: workforceOperationalTypography.table.fontSize,
              lineHeight: workforceOperationalTypography.table.lineHeight,
              paddingVertical: 6,
            }}
          >
            No matching options
          </Text>
        ) : null}
        {showAddRow ? (
          <Pressable
            accessibilityLabel={`Add unlisted option ${trimmed}`}
            accessibilityRole="button"
            accessibilityState={{ disabled }}
            aria-disabled={disabled}
            disabled={disabled}
            onPress={() => toggle(trimmed, true)}
            style={{
              alignItems: "center",
              borderColor: theme.borderSubtle,
              borderRadius: workforceOperationalLayout.controlRadius,
              borderWidth: 1,
              flexDirection: "row",
              gap: 8,
              minHeight: controlHeight,
              paddingHorizontal: 10,
            }}
          >
            <LineIcon
              color={theme.accentStrong}
              name="plus"
              size={16}
              strokeWidth={2.3}
            />
            <Text
              style={{
                color: theme.accentStrong,
                flex: 1,
                fontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                fontWeight: "700",
              }}
            >
              Add “{trimmed}”
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

interface ResponsiveQuestionnaireDateFieldProps {
  controlHeight: number;
  disabled: boolean;
  fontFamily: string;
  label: string;
  onCommit: (text: string) => void;
  onDirtyChange: (dirty: boolean) => void;
  theme: AppShellTheme;
  value: string;
}

function ResponsiveQuestionnaireDateField({
  controlHeight,
  disabled,
  fontFamily,
  label,
  onCommit,
  onDirtyChange,
  theme,
  value,
}: ResponsiveQuestionnaireDateFieldProps): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  const [openMonth, setOpenMonth] = useState<{
    monthIndex: number;
    year: number;
  } | null>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (disabled) {
      setOpenMonth(null);
    }
  }, [disabled]);

  const selectedParts = parseIsoDateText(draft);
  const selectedIso = selectedParts?.isoDate ?? null;
  const calendarWeeks = useMemo(
    () =>
      openMonth
        ? buildMonthGrid(openMonth.year, openMonth.monthIndex)
        : null,
    [openMonth],
  );

  const commitDraft = (): void => {
    if (disabled) {
      return;
    }

    if (draft !== value) onCommit(draft);
  };

  const toggleCalendar = (): void => {
    if (disabled) {
      return;
    }

    if (openMonth) {
      setOpenMonth(null);
      return;
    }
    const today = new Date();
    setOpenMonth(
      selectedParts
        ? {
            monthIndex: selectedParts.monthIndex,
            year: selectedParts.year,
          }
        : {
            monthIndex: today.getMonth(),
            year: today.getFullYear(),
          },
    );
  };

  const shiftMonth = (offset: number): void => {
    if (!openMonth) return;
    const next = new Date(
      Date.UTC(openMonth.year, openMonth.monthIndex + offset, 1),
    );
    setOpenMonth({
      monthIndex: next.getUTCMonth(),
      year: next.getUTCFullYear(),
    });
  };

  const pickDay = (isoDate: string): void => {
    if (disabled) {
      return;
    }

    setDraft(isoDate);
    if (isoDate !== value) {
      onDirtyChange(true);
      onCommit(isoDate);
    }
    setOpenMonth(null);
  };

  return (
    <View
      pointerEvents={disabled ? "none" : "auto"}
      style={{
        gap: workforceConfigurationDialog.fieldLabelGap,
        marginTop: workforceConfigurationDialog.fieldLabelGap,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <View style={{ alignItems: "center", flexDirection: "row", gap: 8 }}>
        <TextInput
          accessibilityLabel={label}
          editable={!disabled}
          onBlur={commitDraft}
          onChangeText={(next) => {
            setDraft(next);
            onDirtyChange(next !== value);
            onCommit(next);
          }}
          onSubmitEditing={commitDraft}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={theme.textTertiary}
          style={{
            backgroundColor: theme.surface,
            borderColor: theme.borderStrong,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            flex: 1,
            fontFamily,
            fontSize: workforceOperationalTypography.body.fontSize,
            minHeight: controlHeight,
            paddingHorizontal: 11,
            paddingVertical: 8,
          }}
          value={draft}
        />
        <Pressable
          accessibilityLabel={`Open calendar for ${label}`}
          accessibilityRole="button"
          accessibilityState={{
            disabled,
            expanded: openMonth != null,
          }}
          aria-disabled={disabled}
          aria-expanded={openMonth != null}
          disabled={disabled}
          onPress={toggleCalendar}
          style={{
            alignItems: "center",
            backgroundColor: openMonth ? theme.accentSubtle : theme.surface,
            borderColor: openMonth
              ? theme.accentStrong
              : theme.borderStrong,
            borderRadius: workforceOperationalLayout.controlRadius,
            borderWidth: 1,
            height: controlHeight,
            justifyContent: "center",
            width: controlHeight,
          }}
        >
          <LineIcon
            color={theme.accentStrong}
            name="appointment"
            size={18}
            strokeWidth={2.2}
          />
        </Pressable>
      </View>

      {openMonth ? (
        <View
          style={{
            backgroundColor: theme.backgroundSubtle,
            borderColor: theme.borderSubtle,
            borderRadius: workforceOperationalLayout.panelRadius,
            borderWidth: 1,
            gap: 5,
            maxWidth: 360,
            padding: 8,
            width: "100%",
          }}
        >
          <View
            style={{
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Pressable
              accessibilityLabel="Previous month"
              accessibilityRole="button"
              onPress={() => shiftMonth(-1)}
              style={{
                alignItems: "center",
                borderRadius: workforceOperationalLayout.controlRadius,
                height: controlHeight,
                justifyContent: "center",
                width: controlHeight,
              }}
            >
              <View style={{ transform: [{ rotate: "180deg" }] }}>
                <LineIcon
                  color={theme.accentStrong}
                  name="chevron-right"
                  size={17}
                  strokeWidth={2.3}
                />
              </View>
            </Pressable>
            <Text
              style={{
                color: theme.textPrimary,
                fontFamily,
                fontSize: workforceOperationalTypography.body.fontSize,
                fontWeight: "700",
                lineHeight: workforceOperationalTypography.body.lineHeight,
              }}
            >
              {formatMonthTitleText(
                openMonth.year,
                openMonth.monthIndex,
              )}
            </Text>
            <Pressable
              accessibilityLabel="Next month"
              accessibilityRole="button"
              onPress={() => shiftMonth(1)}
              style={{
                alignItems: "center",
                borderRadius: workforceOperationalLayout.controlRadius,
                height: controlHeight,
                justifyContent: "center",
                width: controlHeight,
              }}
            >
              <LineIcon
                color={theme.accentStrong}
                name="chevron-right"
                size={17}
                strokeWidth={2.3}
              />
            </Pressable>
          </View>
          <View style={{ flexDirection: "row" }}>
            {["S", "M", "T", "W", "T", "F", "S"].map(
              (weekday, index) => (
                <Text
                  key={`weekday-${String(index)}`}
                  style={{
                    color: theme.textSecondary,
                    flex: 1,
                    fontFamily,
                    fontSize:
                      workforceOperationalTypography.metadata.fontSize,
                    fontWeight: "700",
                    lineHeight:
                      workforceOperationalTypography.metadata.lineHeight,
                    textAlign: "center",
                  }}
                >
                  {weekday}
                </Text>
              ),
            )}
          </View>
          {(calendarWeeks ?? []).map((week, weekIndex) => (
            <View
              key={`week-${String(weekIndex)}`}
              style={{ flexDirection: "row" }}
            >
              {week.map((cell) => {
                const isSelected = cell.isoDate === selectedIso;
                return (
                  <Pressable
                    accessibilityLabel={`Select ${cell.isoDate}`}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isSelected }}
                    aria-pressed={isSelected}
                    key={cell.isoDate}
                    onPress={() => pickDay(cell.isoDate)}
                    style={{
                      alignItems: "center",
                      backgroundColor: isSelected
                        ? theme.accent
                        : "transparent",
                      borderRadius:
                        workforceOperationalLayout.controlRadius,
                      flex: 1,
                      justifyContent: "center",
                      minHeight: controlHeight,
                    }}
                  >
                    <Text
                      style={{
                        color: isSelected
                          ? theme.accentContrast
                          : cell.inMonth
                            ? theme.textPrimary
                            : theme.textTertiary,
                        fontFamily,
                        fontSize:
                          workforceOperationalTypography.table.fontSize,
                        fontWeight: isSelected
                          ? "800"
                          : cell.inMonth
                            ? "600"
                            : "400",
                        lineHeight:
                          workforceOperationalTypography.table.lineHeight,
                      }}
                    >
                      {cell.dayNumber}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

function formatPrefillDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return ` · ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

interface TextAnswerRowProps {
  controlHeight: number;
  disabled: boolean;
  fontFamily: string;
  label: string;
  value: string;
  numeric: boolean;
  theme: AppShellTheme;
  onCommit: (text: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}

// Keep a local draft for responsive typing, while mirroring every edit to the
// parent snapshot so Save cannot miss a focused field that has not blurred.
function TextAnswerRow({
  controlHeight,
  disabled,
  fontFamily,
  label,
  value,
  numeric,
  theme,
  onCommit,
  onDirtyChange,
}: TextAnswerRowProps): React.JSX.Element {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commitDraft = (): void => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <TextInput
      editable={!disabled}
      value={draft}
      onChangeText={(next) => {
        setDraft(next);
        onDirtyChange(next !== value);
        onCommit(next);
      }}
      onBlur={commitDraft}
      onSubmitEditing={commitDraft}
      keyboardType={numeric ? "numeric" : "default"}
      placeholderTextColor={theme.textTertiary}
      accessibilityLabel={label}
      style={{
        borderWidth: 1,
        borderColor: theme.borderStrong,
        backgroundColor: theme.surface,
        color: theme.textPrimary,
        borderRadius: workforceOperationalLayout.controlRadius,
        fontFamily,
        fontSize: workforceOperationalTypography.body.fontSize,
        minHeight: controlHeight,
        opacity: disabled ? 0.6 : 1,
        paddingHorizontal: 11,
        paddingVertical: 8,
        marginTop: workforceConfigurationDialog.fieldLabelGap,
      }}
    />
  );
}
